import type { ComponentProps, ReactElement } from "react";
import { useCallback, useMemo } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import type { ColumnsType } from "@similarweb/pro-logic-components";

import { useUnmount } from "app/custom-hooks/useUnmount";
import { ErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import { SearchResultsTable, TableFallback } from "app/pages/sales-intelligence/common-components/search-results-table";
import {
    StyledExpandedRowContent,
} from "app/pages/sales-intelligence/common-components/search-results-table/components/styles";
import {
    useMeasuredHeight,
} from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useMeasuredHeight";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { useOverwriteWheelEvent } from "app/pages/sales-intelligence/hooks/useOverwriteWheelEvent";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import type { NonEmptyString } from "app/pages/sales-intelligence/types/primitives";
import type { RootState } from "single-spa/store/types";

import CrmRecordsTableHeader from "../../../components/CrmRecordsTableHeader/CrmRecordsTableHeader";
import useFetchCrmRecordsTableResults from "../../../hooks/useFetchCrmRecordsTableResults";
import { setExpandedRowPage, setExpandedRowSort, toggleExpandedRow } from "../../../store/action-creators";
import { selectExpandedRowKeys, selectExpandedRowState, selectExpandedRowVariant } from "../../../store/selectors";
import type { LeadsSearchRowWithKey } from "../../../types";
import type { CrmRecordsTableData, CrmRecordsTableRow } from "../../../types/table";

import {
    getLoadingRowsNumber
} from "./utils";

const DEFAULT_TABLE_DATA: CrmRecordsTableData = { columns: [], rows: [], totalCount: 0, matchedCount: 0 };

type TableProps = ComponentProps<typeof SearchResultsTable<CrmRecordsTableRow>>;

type Props<R> = {
    record: R;
    domain: NonEmptyString;
    integrationProvider: CrmIntegrationProvider;
};

const CrmRecordsTableContainerBase = <R extends LeadsSearchRowWithKey>({
   record,
   domain,
   integrationProvider,
}: Props<R>) => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();

    const { page, pageSize } = useSelector(selectExpandedRowState);

    const { data = DEFAULT_TABLE_DATA, isFetching } = useFetchCrmRecordsTableResults(domain, integrationProvider);

    const expandedRowRef = useOverwriteWheelEvent();

    const [contentRef, contentHeight] = useMeasuredHeight<HTMLDivElement>();

    const paginationProps: TableProps["pagination"] = useMemo(
        () => ({
            pageSize,
            total: data.matchedCount,
            current: page,
            onChange(_currentPage: number, _pageSize: number, nextPage) {
                dispatch(setExpandedRowPage(nextPage));
            },
        }),
        [pageSize, page, data.matchedCount, dispatch],
    );

    const handleSortChange = useCallback<NonNullable<TableProps["onSort"]>>(
        ({ order, columnKey }) => {
            dispatch(setExpandedRowSort({ columnKey: columnKey as string, order }));
        },
        [dispatch],
    );

    const handleHide = useCallback(() => {
        dispatch(toggleExpandedRow({
            key: record.key,
            variant: { kind: "crm-records", integrationProvider: integrationProvider },
        }));
    }, [dispatch, record.key, integrationProvider]);

    useUnmount(() => {
        const state = store.getState();
        const variant = selectExpandedRowVariant(state);
        const isStillThisCrmRow =
            selectExpandedRowKeys(state).includes(record.key) &&
            variant?.kind === "crm-records";

        if (isStillThisCrmRow) {
            handleHide();
        }
    });

    return (
        <StyledExpandedRowContent
            data-automation="crm-records-table"
            ref={expandedRowRef}
            $minHeight={contentHeight}
        >
            <div ref={contentRef}>
                <CrmRecordsTableHeader
                    isLoading={isFetching}
                    companyData={{
                        favicon: record.favicon,
                        companyName: record.company ?? domain,
                        companyOverviewLink: siNavigator.getCompanyAnalysisOverviewLink({
                            key: domain,
                            areTopLevelDomainsIncluded: true,
                        }),
                    }}
                    integrationProvider={integrationProvider}
                    matchedCount={data.matchedCount}
                    totalCount={data.totalCount}
                    onHide={handleHide} />

                <SearchResultsTable<CrmRecordsTableRow>
                    isLoading={isFetching}
                    data={data.rows as CrmRecordsTableRow[]}
                    columnConfig={data.columns as ColumnsType<CrmRecordsTableRow>}
                    pagination={paginationProps}
                    loaderRowCount={getLoadingRowsNumber(record, integrationProvider)}
                    onSort={handleSortChange}
                />
            </div>
        </StyledExpandedRowContent>
    );
};

const CrmRecordsTableContainer = <R extends LeadsSearchRowWithKey>(props: Props<R>): ReactElement => {
    return (
        <ErrorBoundaryWithLogging fallback={<TableFallback isLoading={false} />}>
            <CrmRecordsTableContainerBase {...props} />
        </ErrorBoundaryWithLogging>
    );
};

export default CrmRecordsTableContainer;
